import { z } from "zod";
import { UserProfile } from "./account.js";

/** "everyone" only resolves when the sender is public -- gated server-side, absent (not disabled)
 * client-side per design.md. */
export const BroadcastTarget = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("users"), userIds: z.array(z.string().min(1)).min(1).max(50) }),
  z.object({ kind: z.literal("group"), groupId: z.string().min(1) }),
  z.object({ kind: z.literal("everyone") }),
]);
export type BroadcastTarget = z.infer<typeof BroadcastTarget>;

/** `sessionId` travels in the route (POST /v1/sessions/:id/broadcasts), never the body -- every
 * broadcast shape in this file is session-scoped by path, the guardrail broadcasts.feature pins.
 * `clientRequestId` is what makes an offline-queued compose exactly-once on reconnect: a repeat
 * with the same id from the same sender returns the first send's outcome, never a second send. */
export const SendBroadcastRequest = z.object({
  clientRequestId: z.string().uuid(),
  target: BroadcastTarget,
  body: z.string().min(1).max(500),
});
export type SendBroadcastRequest = z.infer<typeof SendBroadcastRequest>;

export const BroadcastDeliveryOutcome = z.enum(["online", "push", "undeliverable"]);
export type BroadcastDeliveryOutcome = z.infer<typeof BroadcastDeliveryOutcome>;

/** Per-target delivery reporting, not a silent success toast -- design.md's "sent, 1 undelivered"
 * requirement. */
export const BroadcastSendResponse = z.object({
  id: z.string().min(1),
  resolvedTargetCount: z.number().int().nonnegative(),
  outcomes: z.array(z.object({ userId: z.string().min(1), outcome: BroadcastDeliveryOutcome })),
});
export type BroadcastSendResponse = z.infer<typeof BroadcastSendResponse>;

/** GET /v1/sessions/:id/broadcasts?viewerId=... -- the ONLY broadcast read shape that exists. No
 * schema in this file (or anywhere in @proef/contract) is a paginated, non-target-scoped broadcast
 * listing; this is the contract-layer guardrail pin broadcasts.feature's "no discover endpoint"
 * scenario checks by enumerating the package's exports. Never paginated -- bounded to the session's
 * own broadcast count, newest-first. */
export const TargetedBroadcastView = z.object({
  id: z.string().min(1),
  sender: UserProfile,
  body: z.string(),
  createdAt: z.iso.datetime(),
});
export type TargetedBroadcastView = z.infer<typeof TargetedBroadcastView>;

export const TargetedBroadcastsResponse = z.object({
  items: z.array(TargetedBroadcastView),
});
export type TargetedBroadcastsResponse = z.infer<typeof TargetedBroadcastsResponse>;
